/**
 * RSS Feed Selector Component
 *
 * Allows users to select/deselect RSS feeds organized by category
 * Stores preferences in local storage
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button } from '@tracekit/core';
import { CheckCircle, Circle, ChevronDown, ChevronRight, RefreshCw, Search } from 'lucide-react';
import {
  RSS_FEED_REGISTRY,
  RSSFeedCategory,
  CATEGORY_NAMES,
  RSSFeedSource,
  PRIORITY_LABELS,
  FeedSortMode,
  getEnabledFeeds,
  isFeedSelectable,
  loadFeedSortMode,
  saveFeedSortMode,
  sortFeeds,
  sumFeedArticleCounts,
  formatFeedArticleCount,
  loadFeedVolumeStats,
  hasFeedVolumeStats,
  RSSFeedVolumeStats,
} from '../../services/rssFeedRegistry';

interface RSSFeedSelectorProps {
  selectedFeedIds: string[];
  onSelectionChange: (feedIds: string[]) => void;
  articleCountByFeedId?: Record<string, number>;
  volumeStatsUpdatedAt?: string | null;
  onRefreshVolumeStats?: (maxFeeds?: number) => void;
  onVolumeStatsSync?: (stats: RSSFeedVolumeStats) => void;
  isRefreshingVolume?: boolean;
}

const SORT_MODE_LABELS: Record<FeedSortMode, string> = {
  priority: 'Priority',
  volume: 'Volume',
  name: 'Name',
};

export function RSSFeedSelector({
  selectedFeedIds,
  onSelectionChange,
  articleCountByFeedId = {},
  volumeStatsUpdatedAt = null,
  onRefreshVolumeStats,
  onVolumeStatsSync,
  isRefreshingVolume = false,
}: RSSFeedSelectorProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<RSSFeedCategory>>(
    new Set(['government_contracts', 'government_technology', 'defense', 'procurement_usa'])
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState<FeedSortMode>(() => loadFeedSortMode());
  const hasRequestedInitialProbe = useRef(false);

  useEffect(() => {
    const cachedStats = loadFeedVolumeStats();
    onVolumeStatsSync?.(cachedStats);

    if (
      !hasRequestedInitialProbe.current &&
      !hasFeedVolumeStats(cachedStats) &&
      onRefreshVolumeStats
    ) {
      hasRequestedInitialProbe.current = true;
      onRefreshVolumeStats(20);
    }
  }, [onRefreshVolumeStats, onVolumeStatsSync]);

  const feedsByCategory = useMemo(() => {
    return RSS_FEED_REGISTRY.reduce((acc, feed) => {
      if (!acc[feed.category]) {
        acc[feed.category] = [];
      }
      acc[feed.category].push(feed);
      return acc;
    }, {} as Record<RSSFeedCategory, RSSFeedSource[]>);
  }, []);

  const sortedFeedsByCategory = useMemo(() => {
    const sorted: Record<RSSFeedCategory, RSSFeedSource[]> = {} as Record<
      RSSFeedCategory,
      RSSFeedSource[]
    >;

    for (const [category, feeds] of Object.entries(feedsByCategory) as [
      RSSFeedCategory,
      RSSFeedSource[],
    ][]) {
      sorted[category] = sortFeeds(feeds, sortMode, articleCountByFeedId);
    }

    return sorted;
  }, [feedsByCategory, sortMode, articleCountByFeedId]);

  const filteredFeeds = searchTerm
    ? sortFeeds(
        RSS_FEED_REGISTRY.filter(
          (feed) =>
            feed.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            feed.description.toLowerCase().includes(searchTerm.toLowerCase())
        ),
        sortMode,
        articleCountByFeedId
      )
    : null;

  const handleSortModeChange = (mode: FeedSortMode) => {
    setSortMode(mode);
    saveFeedSortMode(mode);
  };

  const toggleCategory = (category: RSSFeedCategory) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleFeed = (feedId: string) => {
    const newSelection = selectedFeedIds.includes(feedId)
      ? selectedFeedIds.filter((id) => id !== feedId)
      : [...selectedFeedIds, feedId];
    onSelectionChange(newSelection);
  };

  const enabledFeeds = getEnabledFeeds();

  const toggleCategory_AllFeeds = (category: RSSFeedCategory) => {
    const categoryFeeds = feedsByCategory[category]
      .filter((feed) => isFeedSelectable(feed))
      .map((feed) => feed.id);
    if (categoryFeeds.length === 0) {
      return;
    }
    const allSelected = categoryFeeds.every((id) => selectedFeedIds.includes(id));

    if (allSelected) {
      onSelectionChange(selectedFeedIds.filter((id) => !categoryFeeds.includes(id)));
    } else {
      onSelectionChange([...new Set([...selectedFeedIds, ...categoryFeeds])]);
    }
  };

  const togglePriorityFeeds = (priority: number) => {
    const priorityFeedIds = enabledFeeds
      .filter((feed) => feed.priority === priority)
      .map((feed) => feed.id);
    if (priorityFeedIds.length === 0) {
      return;
    }
    const allSelected = priorityFeedIds.every((id) => selectedFeedIds.includes(id));

    if (allSelected) {
      onSelectionChange(selectedFeedIds.filter((id) => !priorityFeedIds.includes(id)));
    } else {
      onSelectionChange([...new Set([...selectedFeedIds, ...priorityFeedIds])]);
    }
  };

  const usedPriorities = [...new Set(enabledFeeds.map((feed) => feed.priority))].sort(
    (a, b) => a - b
  );

  const selectAll = () => {
    onSelectionChange(enabledFeeds.map((f) => f.id));
  };

  const deselectAll = () => {
    onSelectionChange([]);
  };

  const hasVolumeStats =
    hasFeedVolumeStats({
      counts: articleCountByFeedId,
      updatedAt: volumeStatsUpdatedAt,
    });

  const volumeStatsLabel = hasVolumeStats
    ? volumeStatsUpdatedAt
      ? `Updated ${new Date(volumeStatsUpdatedAt).toLocaleString()}`
      : `${Object.keys(articleCountByFeedId).length} feeds tracked`
    : 'No volume stats yet';

  const sortAndVolumeControls = (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-700">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 shrink-0">Sort:</span>
      {(Object.keys(SORT_MODE_LABELS) as FeedSortMode[]).map((mode) => (
        <Button
          key={mode}
          variant={sortMode === mode ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => handleSortModeChange(mode)}
          className="text-xs px-2"
        >
          {SORT_MODE_LABELS[mode]}
        </Button>
      ))}
      <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">{volumeStatsLabel}</span>
      {onRefreshVolumeStats && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRefreshVolumeStats?.()}
          disabled={isRefreshingVolume}
          className="text-xs px-2"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isRefreshingVolume ? 'animate-spin' : ''}`} />
          Refresh stats
        </Button>
      )}
    </div>
  );

  if (searchTerm && filteredFeeds) {
    return (
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search feeds..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
          />
        </div>

        {sortAndVolumeControls}

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredFeeds.map((feed) => (
            <FeedItem
              key={feed.id}
              feed={feed}
              selected={selectedFeedIds.includes(feed.id)}
              selectable={isFeedSelectable(feed)}
              articleCountLabel={formatFeedArticleCount(feed, articleCountByFeedId)}
              onToggle={() => toggleFeed(feed.id)}
            />
          ))}
        </div>

        <Button variant="primary" size="sm" onClick={() => setSearchTerm('')}>
          Clear Search
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search feeds..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
          />
        </div>
        <Button variant="primary" size="sm" onClick={selectAll}>
          Select All
        </Button>
        <Button variant="primary" size="sm" onClick={deselectAll}>
          Deselect All
        </Button>
      </div>

      {sortAndVolumeControls}

      <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
          {selectedFeedIds.length} of {enabledFeeds.length} selectable feeds selected
        </span>
        <Badge variant="primary" className="text-xs">
          {Object.keys(feedsByCategory).length} categories
        </Badge>
      </div>

      {usedPriorities.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-700">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 shrink-0">
            By priority:
          </span>
          {usedPriorities.map((priority) => {
            const priorityFeeds = enabledFeeds.filter((feed) => feed.priority === priority);
            const selectedCount = priorityFeeds.filter((feed) =>
              selectedFeedIds.includes(feed.id)
            ).length;
            const allSelected =
              priorityFeeds.length > 0 && selectedCount === priorityFeeds.length;
            const priorityBadge = getPriorityBadge(priority);

            return (
              <div
                key={priority}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1"
              >
                {priorityBadge && (
                  <Badge variant="primary" className={`text-xs ${priorityBadge.className}`}>
                    {PRIORITY_LABELS[priority] ?? `P${priority}`}
                  </Badge>
                )}
                <Badge variant="primary" className="text-xs">
                  {selectedCount}/{priorityFeeds.length}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => togglePriorityFeeds(priority)}
                  className="text-xs px-2"
                >
                  {allSelected ? 'Deselect' : 'Select'}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
        {Object.entries(sortedFeedsByCategory).map(([category, feeds]) => {
          const isExpanded = expandedCategories.has(category as RSSFeedCategory);
          const selectableFeeds = feeds.filter((feed) => isFeedSelectable(feed));
          const selectedCount = selectableFeeds.filter((feed) =>
            selectedFeedIds.includes(feed.id)
          ).length;
          const allSelected =
            selectableFeeds.length > 0 && selectedCount === selectableFeeds.length;
          const categoryArticleTotal = sumFeedArticleCounts(feeds, articleCountByFeedId);

          return (
            <div
              key={category}
              className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden"
            >
              <div className="bg-gray-50 dark:bg-slate-800 p-3">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => toggleCategory(category as RSSFeedCategory)}
                    className="flex items-center gap-2 flex-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-semibold text-sm">
                      {CATEGORY_NAMES[category as RSSFeedCategory]}
                    </span>
                    <Badge variant="primary" className="text-xs">
                      {selectedCount}/{selectableFeeds.length}
                    </Badge>
                    {categoryArticleTotal > 0 && (
                      <Badge
                        variant="primary"
                        className="text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                      >
                        {categoryArticleTotal} articles
                      </Badge>
                    )}
                  </button>
                  {selectableFeeds.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCategory_AllFeeds(category as RSSFeedCategory)}
                      className="text-xs"
                    >
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="p-2 space-y-1">
                  {feeds.map((feed) => (
                    <FeedItem
                      key={feed.id}
                      feed={feed}
                      selected={selectedFeedIds.includes(feed.id)}
                      selectable={isFeedSelectable(feed)}
                      articleCountLabel={formatFeedArticleCount(feed, articleCountByFeedId)}
                      onToggle={() => toggleFeed(feed.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getPriorityBadge(priority: number) {
  if (priority === 1) {
    return {
      label: 'High Priority',
      className:
        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-300 dark:border-red-700',
    };
  }
  if (priority === 2) {
    return {
      label: 'Medium Priority',
      className:
        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700',
    };
  }
  if (priority === 3) {
    return {
      label: 'Standard',
      className:
        'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 dark:border-gray-600',
    };
  }
  return null;
}

interface FeedItemProps {
  feed: RSSFeedSource;
  selected: boolean;
  selectable: boolean;
  articleCountLabel: string | null;
  onToggle: () => void;
}

function FeedItem({ feed, selected, selectable, articleCountLabel, onToggle }: FeedItemProps) {
  const Icon = selected ? CheckCircle : Circle;
  const priorityBadge = getPriorityBadge(feed.priority);

  return (
    <button
      type="button"
      onClick={selectable ? onToggle : undefined}
      disabled={!selectable}
      className={`w-full flex items-start gap-3 p-2 rounded-lg transition-colors text-left ${
        selectable
          ? 'hover:bg-gray-100 dark:hover:bg-slate-700'
          : 'opacity-50 cursor-not-allowed'
      }`}
    >
      <Icon
        className={`h-5 w-5 mt-0.5 shrink-0 ${
          selected ? 'text-green-600' : 'text-gray-400'
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
            {feed.name}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {articleCountLabel !== null && (
              <Badge
                variant="primary"
                className={`text-xs ${
                  selectable
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                    : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {articleCountLabel}
              </Badge>
            )}
            {priorityBadge && (
              <Badge variant="primary" className={`text-xs ${priorityBadge.className}`}>
                {priorityBadge.label}
              </Badge>
            )}
            {!selectable && feed.health_status && (
              <Badge
                variant="primary"
                className="text-xs bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
              >
                {feed.health_status}
              </Badge>
            )}
            <Badge variant="primary" className="text-xs">
              {feed.updateFrequency}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
          {feed.description}
        </p>
      </div>
    </button>
  );
}
